import type { ChangeEvent } from "react"
import React, { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"

import * as Yup from "yup"
import { DialogContent, Grid } from "@mui/material"
import { Button, Checkbox, InputText, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"
import { t } from "i18next"
import type { MRT_ColumnDef, MRT_PaginationState } from "material-react-table"

import { SideBar } from "../../components/UI/SideBar"
import { InfoBlock } from "../../components/containers/InfoBlock"
import { DebouncedInput } from "../../components/form/DebouncedInput"
import { BaseTable } from "../../components/tables/BaseTable"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { DeploymentList } from "../../types/generated/api/DeploymentList"
import type { PaginatedDeploymentListList } from "../../types/generated/api/PaginatedDeploymentListList"
import type { KonanResource } from "../../types/generated/authentication/KonanResource"
import { TypeEnum } from "../../types/generated/authentication/TypeEnum"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import type { GroupDialogProps } from "./Interfaces"

import styles from "./Groups.module.scss"

// Yup validation schema object
const groupValidationSchema = Yup.object({
  groupName: Yup.string().required(t("GroupCreationDialog.validation.roleNameRequired", { ns: "settings" })),
  selectedProjects: Yup.array()
    .required()
    .test({
      test: (arr) => (arr as KonanResource[]).length > 0,
    }),
})

export function GroupCreationDialog(props: Readonly<GroupDialogProps>): React.ReactElement {
  const { isOpen, onClose, onAccept, group, isViewMode } = props

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const { t } = useTranslation("settings")

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const [isReadMode, setIsReadMode] = useState<boolean>(isViewMode ?? Boolean(group) ?? false)

  // Permission check constants
  const canUpdateGroups = useIsPermitted("Update groups", permissions.synapse_id, flattenedSidPermissions)
  const canCreateGroups = useIsPermitted("Create groups", permissions.synapse_id, flattenedSidPermissions)

  const filterOptions = [
    {
      label: t("groupCreationDialog.filter.all"),
      value: "All",
    },
    {
      label: t("groupCreationDialog.filter.selected"),
      value: "Selected",
    },
    {
      label: t("groupCreationDialog.filter.unselected"),
      value: "Unselected",
    },
  ] as const
  const [searchValue, setSearchValue] = useState<string>("")

  const [filter, setFilter] = useState<(typeof filterOptions)[number]["value"]>("All")

  // Backend pagination
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // Local pagination (Selected)
  const [localPagination, setLocalPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // Store full selected projects in state for local pagination (only used in create mode)
  const [selectedProjectsState, setSelectedProjectsState] = useState<DeploymentList[]>([])

  // Fetch projects from backend
  const { isFetching: isProjectsFetching, data: projects } = useQuery<
    AxiosResponse<PaginatedDeploymentListList>,
    AxiosError
  >(
    ["projects", pagination.pageIndex + 1, pagination.pageSize, searchValue],
    () =>
      KonanAPI.fetchProjects({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        search: searchValue,
      }),
    { keepPreviousData: true, enabled: !!isOpen },
  )

  const formik = useFormik({
    initialValues: {
      groupName: group?.name ?? "",
      selectedProjects: group?.konan_resources ?? [],
    },
    validationSchema: groupValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      await onAccept?.(values.groupName, values.selectedProjects)

      resetForm({})
      onClose()
    },
  })

  const columns: MRT_ColumnDef<DeploymentList>[] = [
    {
      header: "Project",
      Cell: ({ row }) => {
        const isChecked = !!formik.values.selectedProjects.find(
          (project: KonanResource) => project.uid === row.original.uuid,
        )

        return (
          <Checkbox
            key={row.original.name}
            id={row.original.name}
            label={row.original.name}
            name={row.original.name}
            value={row.original.uuid}
            checked={isChecked}
            readOnly={isReadMode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (!isReadMode) {
                if (e.target?.checked) {
                  // Add to formik
                  formik.setFieldValue("selectedProjects", [
                    ...formik.values.selectedProjects,
                    { uid: row.original.uuid, type: TypeEnum.PROJECT, name: row.original.name },
                  ])

                  if (!group) {
                    const selectedProject = projects?.data?.results?.find(
                      (project: DeploymentList) => project.uuid === row.original.uuid,
                    )
                    if (selectedProject) {
                      setSelectedProjectsState((prev) =>
                        prev.find((p) => p.uuid === selectedProject.uuid) ? prev : [...prev, selectedProject],
                      )
                    }
                  }
                } else {
                  // Remove from formik
                  formik.setFieldValue(
                    "selectedProjects",
                    formik.values.selectedProjects.filter((item: KonanResource) => item.uid !== row.original.uuid),
                  )

                  if (!group) {
                    setSelectedProjectsState((prev) => prev.filter((p) => p.uuid !== row.original.uuid))
                  }
                }
              }
            }}
          />
        )
      },
    },
  ]

  // Filter & paginate
  const filteredTable = useMemo(() => {
    let data: DeploymentList[] = []
    let rowCount = 0
    let currentPagination = pagination
    let setCurrentPagination = setPagination

    if (group) {
      data = projects?.data?.results ?? []
      rowCount = projects?.data?.count ?? 0
      currentPagination = pagination
      setCurrentPagination = setPagination
    } else {
      switch (filter) {
        case "All":
          data = projects?.data?.results ?? []
          rowCount = projects?.data?.count ?? 0
          currentPagination = pagination
          setCurrentPagination = setPagination
          break

        case "Selected":
          let filteredSelected = selectedProjectsState

          if (searchValue) {
            const searchLower = searchValue.toLowerCase()
            filteredSelected = filteredSelected.filter((sp) => sp.name.toLowerCase().startsWith(searchLower))
          }

          rowCount = filteredSelected.length

          const startSel = localPagination.pageIndex * localPagination.pageSize
          const endSel = startSel + localPagination.pageSize

          data = filteredSelected.slice(startSel, endSel)

          currentPagination = localPagination
          setCurrentPagination = setLocalPagination
          break

        case "Unselected":
          const unselectedProjects = (projects?.data?.results ?? []).filter(
            (proj) => !formik.values.selectedProjects.find((sp) => sp.uid === proj.uuid),
          )
          data = unselectedProjects
          rowCount = projects?.data?.count ?? 0
          currentPagination = pagination
          setCurrentPagination = setPagination
          break
      }
    }

    return (
      <BaseTable
        title="Projects"
        disableToolbar
        data={data}
        isLoading={isProjectsFetching}
        columns={columns as MRT_ColumnDef[]}
        rowCount={rowCount}
        enableTableHead={false}
        enableOrdering={false}
        hideRowCount
        enableHiding={false}
        enableFiltering={false}
        manualPagination
        pagination={currentPagination}
        setPagination={setCurrentPagination}
      />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    group, // Add group as dependency
    filter,
    searchValue,
    projects?.data?.results,
    projects?.data?.count,
    selectedProjectsState,
    formik.values.selectedProjects,
    isProjectsFetching,
    pagination,
    localPagination,
  ])

  useEffect(() => {
    if (group && projects?.data?.results) {
      const selectedUids = group.konan_resources?.map((project) => project.uid) ?? []

      if (!group) {
        const newSelectedProjects = projects.data.results.filter((project) => selectedUids.includes(project.uuid))

        setSelectedProjectsState((prevState) => {
          const existingUids = prevState.map((p) => p.uuid)
          const projectsToAdd = newSelectedProjects.filter((p) => !existingUids.includes(p.uuid))
          return [...prevState, ...projectsToAdd]
        })
        const formikSelectedProjects = selectedUids
          .map((uid) => {
            const matchingProject = projects?.data?.results?.find((p) => p.uuid === uid)
            return (
              matchingProject && {
                uid: uid,
                type: TypeEnum.PROJECT,
                name: matchingProject.name,
              }
            )
          })
          .filter(Boolean)

        if (JSON.stringify(formik.values.selectedProjects) !== JSON.stringify(formikSelectedProjects)) {
          formik.setFieldValue("selectedProjects", formikSelectedProjects)
        }
      }
    }
  }, [formik, group, projects?.data?.results])

  return (
    <SideBar
      title={t(
        group
          ? isReadMode
            ? "groupCreationDialog.dialogTitle.view"
            : "groupCreationDialog.dialogTitle.edit"
          : "groupCreationDialog.dialogTitle.new",
      )}
      isOpen={isOpen}
      onClose={() => {
        formik.resetForm()
        onClose()
      }}
      closeIcon={{
        icon: "close",
        position: "right",
      }}
      variant="floating"
      actions={[
        <Button
          variant="secondary"
          key="cancel-btn"
          onClick={() => {
            formik.resetForm()
            onClose()
          }}
        >
          {t("groupCreationDialog.buttons.cancel")}
        </Button>,
        <>
          {isReadMode && canUpdateGroups ? (
            <Button onClick={() => setIsReadMode(false)} variant="primary">
              {t("groupCreationDialog.buttons.edit")}
            </Button>
          ) : (
            !isReadMode &&
            (canUpdateGroups || canCreateGroups) && (
              <Button
                onClick={formik.submitForm}
                disabled={formik.isSubmitting || !(formik.isValid && formik.dirty)}
                variant="primary"
                isLoading={formik.isSubmitting}
              >
                {t("groupCreationDialog.buttons.save")}
              </Button>
            )
          )}
        </>,
      ]}
      paperStyle={{
        width: "40%",
      }}
    >
      <DialogContent className={styles.groupDialogContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {isReadMode ? (
              <InfoBlock
                text={formik.values.groupName}
                title={t("groupCreationDialog.formFields.groupName.label")}
                type="default"
                variant="base"
              />
            ) : (
              <InputText
                label={t("groupCreationDialog.formFields.groupName.label")}
                id="groupName"
                value={formik.values.groupName}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                placeholder={t("groupCreationDialog.formFields.groupName.placeholder")}
                description={t("groupCreationDialog.formFields.groupName.description")}
                fullWidth
                required
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Grid item xs={12} mb={1}>
              <Typography variant={"label-bold"}>
                {isReadMode
                  ? t("groupCreationDialog.formFields.projects.viewTitle")
                  : t("groupCreationDialog.formFields.projects.editTitle")}
              </Typography>
            </Grid>
            <Grid item xs={12} mb={1}>
              <DebouncedInput
                isSearch
                placeHolder={t("groupCreationDialog.formFields.projects.searchPlaceholder")}
                variant="filled"
                callback={(value: string | null) => {
                  if (filter !== "Selected") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
                  } else {
                    setLocalPagination((prev) => ({ ...prev, pageIndex: 0 }))
                  }
                  setSearchValue(() => value as string)
                }}
              />
            </Grid>
            <Grid
              item
              container
              xs={12}
              mb={1}
              gap={1}
              alignItems={"center"}
              justifyContent={group ? "end" : "space-between"}
            >
              {!group && (
                <Grid item display={"flex"} alignItems={"center"} gap={1}>
                  {filterOptions.map((opt, idx) => (
                    <Button
                      onClick={() => {
                        setFilter(opt.value)
                        setLocalPagination({ pageIndex: 0, pageSize: 10 })
                      }}
                      key={idx + opt.value}
                      size="small"
                      variant={filter === opt.value ? "primary-inverse" : "secondary"}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Grid>
              )}
              <Grid item display={"flex"} alignItems={"center"} gap={1} justifySelf={"end"}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    formik.setFieldValue("selectedProjects", [])
                    if (!group) {
                      setSelectedProjectsState([])
                    }
                  }}
                  disabled={formik.values.selectedProjects.length === 0}
                >
                  {t("groupCreationDialog.buttons.unselectAll")}
                </Button>
                <Typography variant={"label-bold"}>
                  {formik.values.selectedProjects.length} {t("groupCreationDialog.labels.selected")}
                </Typography>
              </Grid>
            </Grid>

            {filteredTable}
          </Grid>
        </Grid>
      </DialogContent>
    </SideBar>
  )
}
